import React, { useMemo, useState } from "react";
import { Instagram, GitCompareArrows } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatMoney } from "@/lib/api";
import { PRICING_DISCLAIMER } from "@/lib/pricing";

const TIERS = [
    { key: "basico", label: "Básico" },
    { key: "normal", label: "Estándar" },
    { key: "premium", label: "Premium" },
];

function normalizeInstagram(url) {
    const v = String(url || "").trim();
    if (!v) return "";
    if (v.startsWith("http://") || v.startsWith("https://")) return v;
    return `https://www.instagram.com/${v.replace(/^@/, "")}`;
}

function priceLabel(p) {
    if (!p) return "—";
    if (p.price_mode === "per_guest") return `${formatMoney(p.price)} / pax`;
    if (p.price_mode === "per_module") return `${formatMoney(p.price)} / módulo`;
    return formatMoney(p.price);
}

export default function ProviderMatrix({
    categoryKey,
    categoryLabel,
    providers,
    selectedId,
    onSelect,
    guests,
    disabled = false,
    disabledReason = "",
}) {
    const filtered = useMemo(
        () => providers.filter((p) => p.category === categoryKey && p.active !== false),
        [providers, categoryKey]
    );

    const brands = useMemo(() => {
        const map = new Map();
        for (const p of filtered) {
            const key = p.brand || p.name;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(p);
        }
        return Array.from(map.entries()).map(([name, pkgs]) => ({
            name,
            pkgs,
            instagram: pkgs.find((x) => x.instagram_url)?.instagram_url,
            logo: pkgs.find((x) => x.logo_url)?.logo_url,
            desc: (pkgs[0]?.description || "").split(".")[0],
            minPrice: Math.min(...pkgs.map((x) => x.price)),
            perGuest: pkgs[0]?.price_mode === "per_guest",
        }));
    }, [filtered]);

    const [openBrand, setOpenBrand] = useState(null);
    const [compareSel, setCompareSel] = useState([]);
    const [showCompare, setShowCompare] = useState(false);

    const selectedProvider = filtered.find((p) => p.id === selectedId);
    const selectedBrandName = selectedProvider ? (selectedProvider.brand || selectedProvider.name) : null;

    if (disabled) {
        return (
            <div className="mb-10" data-testid={`matrix-${categoryKey}`}>
                <div className="flex items-baseline justify-between border-b border-foreground pb-2 mb-5">
                    <h3 className="font-display text-2xl md:text-3xl">{categoryLabel}</h3>
                    <span className="overline text-accent">🔒 Bloqueado</span>
                </div>
                <div className="bg-secondary border-l-2 border-accent p-6 text-sm">
                    <div className="overline text-accent mb-2">Incluido en Wedding Planner &quot;Todo Incluido&quot;</div>
                    <p className="text-muted-foreground">{disabledReason}</p>
                </div>
            </div>
        );
    }

    if (brands.length === 0) {
        return (
            <div className="mb-10" data-testid={`matrix-${categoryKey}`}>
                <div className="flex items-baseline justify-between border-b border-foreground pb-2 mb-5">
                    <h3 className="font-display text-2xl md:text-3xl">{categoryLabel}</h3>
                    <span className="overline text-muted-foreground">Próximamente</span>
                </div>
                <div className="bg-white/60 border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid={`matrix-empty-${categoryKey}`}>
                    Los proveedores de {categoryLabel} están siendo curados por Hacienda Nápoles y se cargarán próximamente. Esta categoría no afecta tu cotización actual.
                </div>
            </div>
        );
    }

    const toggleCompare = (brandName) => {
        setCompareSel((prev) => prev.includes(brandName)
            ? prev.filter((b) => b !== brandName)
            : (prev.length < 3 ? [...prev, brandName] : prev));
    };
    const compareBrands = brands.filter((b) => compareSel.includes(b.name));

    return (
        <div className="mb-10" data-testid={`matrix-${categoryKey}`}>
            <div className="flex flex-wrap items-baseline justify-between border-b border-foreground pb-2 mb-5 gap-3">
                <h3 className="font-display text-2xl md:text-3xl">{categoryLabel}</h3>
                <div className="flex items-center gap-3">
                    {compareSel.length >= 2 && (
                        <button
                            type="button"
                            onClick={() => setShowCompare(true)}
                            className="inline-flex items-center gap-1.5 bg-accent text-accent-foreground px-3 py-1.5 text-[11px] overline"
                            data-testid={`btn-compare-${categoryKey}`}
                        >
                            <GitCompareArrows className="h-3.5 w-3.5" /> Comparar ({compareSel.length})
                        </button>
                    )}
                    <span className="overline text-muted-foreground hidden md:inline">1 tarjeta = 1 proveedor · marca ✓ para comparar</span>
                </div>
            </div>

            {/* 1 tarjeta = 1 proveedor */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {brands.map((b) => {
                    const isChosen = selectedBrandName === b.name;
                    const chosenPkg = isChosen ? selectedProvider : null;
                    return (
                        <div
                            key={b.name}
                            role="button"
                            tabIndex={0}
                            onClick={() => setOpenBrand(b)}
                            onKeyDown={(e) => e.key === "Enter" && setOpenBrand(b)}
                            data-testid={`provider-card-${categoryKey}-${b.name.replace(/\s+/g, '-')}`}
                            className={`tier-card text-left bg-white border p-5 cursor-pointer relative ${isChosen ? "border-primary bg-secondary" : "border-border hover:border-foreground"}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                {b.logo
                                    ? <img src={b.logo} alt={b.name} className="h-10 object-contain" />
                                    : <span className="overline text-muted-foreground">{categoryLabel}</span>}
                                <span onClick={(e) => e.stopPropagation()} className="flex items-center gap-1" title="Comparar">
                                    <Checkbox
                                        checked={compareSel.includes(b.name)}
                                        onCheckedChange={() => toggleCompare(b.name)}
                                        className="rounded-none"
                                        data-testid={`compare-check-${categoryKey}-${b.name.replace(/\s+/g, '-')}`}
                                    />
                                </span>
                            </div>
                            <h4 className="font-serif italic text-xl mt-2 leading-tight">{b.name}</h4>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{b.desc}.</p>
                            <div className="mt-3 flex items-baseline justify-between">
                                <span className="tabular text-sm font-medium">
                                    Desde {formatMoney(b.minPrice)}{b.perGuest ? " /pax" : ""}
                                </span>
                                <span className="overline text-[10px] text-accent underline-editorial">Ver paquetes →</span>
                            </div>
                            {b.instagram && (
                                <a
                                    href={b.instagram}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1.5 mt-3 text-[11px] text-muted-foreground hover:text-foreground"
                                    data-testid={`instagram-${categoryKey}-${b.name.replace(/\s+/g, '-')}`}
                                >
                                    <Instagram className="h-3.5 w-3.5 text-accent" /> Ver Instagram / Portafolio
                                </a>
                            )}
                            {isChosen && (
                                <div className="absolute top-3 right-10 bg-accent text-accent-foreground px-2 py-0.5 text-[10px] overline" data-testid={`chosen-badge-${categoryKey}`}>
                                    ✓ {TIERS.find((t) => t.key === chosenPkg?.tier)?.label || "Elegido"}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <p className="mt-4 text-xs font-medium text-accent leading-snug border-l-2 border-accent pl-3">
                {PRICING_DISCLAIMER}
            </p>

            {/* Modal: paquetes del proveedor */}
            <Dialog open={!!openBrand} onOpenChange={(o) => !o && setOpenBrand(null)}>
                <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto bg-background" data-testid={`brand-dialog-${categoryKey}`}>
                    {openBrand && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-3xl font-semibold flex items-center gap-3">
                                    {openBrand.logo && <img src={openBrand.logo} alt="" className="h-10 object-contain" />}
                                    {openBrand.name}
                                </DialogTitle>
                            </DialogHeader>
                            {openBrand.instagram && (
                                <a href={normalizeInstagram(openBrand.instagram)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs underline-editorial -mt-2" data-testid="brand-instagram-link">
                                    <Instagram className="h-3.5 w-3.5 text-accent" /> 📸 Ver Instagram / Portafolio
                                </a>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                                {TIERS.map((tier) => {
                                    const p = openBrand.pkgs.find((x) => x.tier === tier.key);
                                    if (!p) {
                                        return (
                                            <div key={tier.key} className="border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                                                <div className="overline mb-2">{tier.label}</div>Sin paquete
                                            </div>
                                        );
                                    }
                                    const isSel = selectedId === p.id;
                                    return (
                                        <div key={p.id} className={`border p-4 bg-white flex flex-col ${p.all_inclusive ? "border-accent" : isSel ? "border-primary" : "border-border"}`} data-testid={`package-${categoryKey}-${tier.key}`}>
                                            <div className="flex items-center justify-between">
                                                <span className={`overline ${tier.key === "premium" ? "text-accent" : "text-muted-foreground"}`}>{tier.label}</span>
                                                {p.all_inclusive && <span className="bg-accent text-accent-foreground px-1.5 py-0.5 text-[9px] overline">Todo Incluido</span>}
                                            </div>
                                            <h5 className="font-serif italic text-base mt-2 leading-tight">{p.name}</h5>
                                            <div className="tabular font-medium mt-2">{priceLabel(p)}</div>
                                            {p.price_mode === "per_guest" && guests > 0 && (
                                                <div className="text-[11px] text-muted-foreground tabular">≈ {formatMoney(p.price * guests)} para {guests} pax</div>
                                            )}
                                            <ul className="mt-3 space-y-1 text-[11px] text-muted-foreground flex-1">
                                                {(p.included || []).map((inc) => <li key={inc}>· {inc}</li>)}
                                            </ul>
                                            {p.all_inclusive && (
                                                <p className="text-[10px] text-accent mt-2">Al elegirlo se bloquean Catering, Decoración, Música, Fotografía, Video y Estructura.</p>
                                            )}
                                            <Button
                                                onClick={() => { onSelect(categoryKey, p.id); setOpenBrand(null); }}
                                                className={`mt-3 rounded-none w-full ${isSel ? "bg-secondary text-foreground border border-primary hover:bg-secondary" : "bg-primary text-primary-foreground hover:bg-primary/85"}`}
                                                data-testid={`package-select-${categoryKey}-${tier.key}`}
                                            >
                                                {isSel ? "✓ Elegido" : "Elegir este paquete"}
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-[10px] text-muted-foreground italic mt-2">{PRICING_DISCLAIMER}</p>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal: comparador de proveedores del mismo rubro */}
            <Dialog open={showCompare} onOpenChange={setShowCompare}>
                <DialogContent className="rounded-none max-w-4xl max-h-[85vh] overflow-y-auto bg-background" data-testid={`compare-dialog-${categoryKey}`}>
                    <DialogHeader>
                        <DialogTitle className="font-display text-3xl font-semibold">Comparar {categoryLabel}</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b-2 border-foreground">
                                    <th className="text-left py-3 overline text-muted-foreground">Paquete</th>
                                    {compareBrands.map((b) => (
                                        <th key={b.name} className="text-left py-3 font-serif italic text-lg">{b.name}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {TIERS.map((tier) => (
                                    <tr key={tier.key} className="border-b border-border align-top">
                                        <td className="py-4 overline text-muted-foreground">{tier.label}</td>
                                        {compareBrands.map((b) => {
                                            const p = b.pkgs.find((x) => x.tier === tier.key);
                                            if (!p) return <td key={b.name} className="py-4 text-muted-foreground/60">—</td>;
                                            return (
                                                <td key={b.name} className="py-4 pr-4">
                                                    <div className="tabular font-medium">{priceLabel(p)}</div>
                                                    {p.price_mode === "per_guest" && guests > 0 && (
                                                        <div className="text-[10px] text-muted-foreground tabular">≈ {formatMoney(p.price * guests)}</div>
                                                    )}
                                                    <ul className="mt-1 text-[10px] text-muted-foreground space-y-0.5">
                                                        {(p.included || []).slice(0, 3).map((inc) => <li key={inc}>· {inc}</li>)}
                                                    </ul>
                                                    <button
                                                        type="button"
                                                        onClick={() => { onSelect(categoryKey, p.id); setShowCompare(false); }}
                                                        className="mt-2 overline text-[10px] text-accent underline-editorial"
                                                        data-testid={`compare-select-${categoryKey}-${b.name.replace(/\s+/g, '-')}-${tier.key}`}
                                                    >
                                                        Elegir →
                                                    </button>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
